import { useMemo } from 'react';
import { t } from 'i18next';
import { useDateSelectionMenu } from '../../../store/dateSelectionMenuStore';
import { buildEventCreationFormUrl } from '../../../domain/fullCalendar/fullCalendarUseCase';
import { addIdsToAddFormUrls } from '../../../domain/addElement/addElementUseCase';
import groupOptionsByOptGroup from '../../../utils/optGroup/optGroup';
import Modal, { ModalHeader } from '../Modal/Modal';
import Button from '../../atoms/Button/Button';
import LabelWithIconAndColor from '../LabelWithIconAndColor/LabelWithIconAndColor';
import styles from './DateSelectionModal.module.css';

/**
 * Modal pour choisir un type de formulaire de création après sélection d'une plage.
 * @param {object} props
 * @param {ApiManagerTimelineAddFormUrl[]} props.addFormUrls
 * @param {(formUrl: string) => void} props.onSelect
 * @returns {JSX.Element | null}
 */
function DateSelectionModal({ addFormUrls, onSelect }) {
  const {
    isOpen, selection, closeDateSelectionMenu,
  } = useDateSelectionMenu();

  const groupedOptions = useMemo(() => {
    const withIds = addIdsToAddFormUrls(addFormUrls ?? []);
    return groupOptionsByOptGroup(withIds);
  }, [addFormUrls]);

  const handleClose = () => {
    closeDateSelectionMenu();
  };

  const handleSelect = (/** @type {AddFormUrlWithId} */ item) => {
    if (!selection) return;
    onSelect(buildEventCreationFormUrl(item.url, selection));
    closeDateSelectionMenu();
  };

  if (!addFormUrls?.length) return null;

  return (
    <Modal
      isOpen={isOpen && !!selection}
      onClose={handleClose}
      className={styles.dialog}
      header={(
        <ModalHeader
          title={t('table.addElement')}
          onClose={handleClose}
        />
      )}
    >
      <div className={styles.content}>
        {groupedOptions.map((group) => (
          <section
            key={group.lib || 'default'}
            className={styles.group}
            aria-label={group.lib || undefined}
          >
            {group.lib ? (
              <h3 className={styles['group-title']}>{group.lib}</h3>
            ) : null}
            <div className={styles['options-grid']}>
              {group.options.map((item) => (
                <Button
                  key={item.id}
                  color="no-background"
                  size="small"
                  className={styles['option-button']}
                  onClick={() => handleSelect(item)}
                >
                  <LabelWithIconAndColor
                    label={item.lib}
                    icon={item.icon}
                    iconColor={item.iconColor}
                    hasColorSquareFallback
                  />
                </Button>
              ))}
            </div>
          </section>
        ))}
      </div>
    </Modal>
  );
}

export default DateSelectionModal;
